import UIMultiLevelDropdown from 'UIComponents/dropdown/UIMultiLevelDropdown';
import UIIcon from 'UIComponents/icon/UIIcon';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useGetHasScope, useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import { getIsClassicWorkflow } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import EditorHelpMenuDropdown from 'automation-ui-canvas/components/flow-header/menu-bar/EditorHelpMenuDropdown';
import makeMenuItem from 'automation-ui-canvas/components/flow-header/menu-bar/utils/makeMenuItem';
import { HELP_OPTION_ID } from 'automation-ui-canvas/components/flow-header/menu-bar/menuOptions/helpOptions';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledDropdownContainer = styled.div.withConfig({
  displayName: "EditorMenuBarButton__StyledDropdownContainer",
  componentId: "msq06l-0"
})(["margin:0px;& .editor-menu-bar-button.editor-menu-bar-button{color:", ";background-color:", "!important;border-style:none;&:hover{background-color:", ";}height:48px;margin:0px;padding-left:16px;padding-right:16px;}"], ({
  disabled,
  theme
}) => disabled ? theme.color['text-primary-disabled'] : theme.color['text-primary-default'], ({
  open,
  theme
}) => open ? theme.color['fill-accent-blue-subtle'] : theme.color['fill-surface-default'], ({
  theme
}) => theme.color['fill-accent-blue-subtle']);
const EditorMenuBarButton = ({
  id,
  open,
  disabled = false,
  onMouseEnter,
  onMouseLeave,
  onOpenChange,
  onOptionClick /* fired when an option within a menu is clicked on*/,
  onClick /* fired when the menu item itself is clicked */,
  objectTypeId,
  title,
  options,
  flow,
  topLevelMenuOptions = [],
  Container = StyledDropdownContainer,
  dropdownProps
}) => {
  const buttonEl = useRef(undefined);
  const rootRef = useRef(null);
  const history = useHistory();
  const getIsUngated = useGetIsUngated();
  const getHasScope = useGetHasScope();
  const isClassicWorkflow = Boolean(getIsClassicWorkflow(flow));

  /*
      Generate the options for the UIComponents dropdown from our menu bar model.
      Its okay to use recursion here since the dataset is small. If it were more deepely nested
      we could consider using anb accumulator instead
  */
  const dropdownOptions = useMemo(() => {
    /* Typed as any since UIMultiLevelDropdown types are incomplete */
    const generateOptions = menuOptions => {
      return menuOptions ? menuOptions.map(optionGroup => optionGroup.filter(option => !option.getIsVisible || option.getIsVisible({
        getIsUngated,
        getHasScope,
        isClassicWorkflow,
        flow
      })).map(option => makeMenuItem({
        option,
        nestedOptions: option.options ? generateOptions(option.options) : undefined,
        onOptionClick,
        flow,
        history,
        objectTypeId
      }))) : [];
    };
    return generateOptions(options).filter(optionsArray => optionsArray.length > 0);
  }, [options, objectTypeId, onOptionClick, history, flow, getIsUngated, getHasScope, isClassicWorkflow]);

  /* custom close on outside click handling for buttons with no menu items */
  const handleClickOutside = useCallback(event => {
    /* Only handle clicks when this menu is active. Otherwise this might block clicks in other menus */
    if (!open) {
      return;
    }
    const domNode = rootRef.current;
    if (!options && !domNode || domNode && !domNode.contains(event.target)) {
      onOpenChange({
        id,
        value: false
      });
    }
  }, [id, onOpenChange, options, open]);

  /* Add event listeners so we can close on click outside for buttons with no options */
  useEffect(() => {
    if (!options) {
      document.addEventListener('click', handleClickOutside, true);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [options, handleClickOutside]);
  const defaultDropdownProps = Object.assign({
    minMenuWidth: 220,
    open: !!options && open,
    buttonRef: buttonEl,
    caretRenderer: () => {
      if (options) {
        return /*#__PURE__*/_jsx(UIIcon, {
          name: "down"
        });
      }
      return null;
    },
    onOpenChange: ({
      target: {
        value
      }
    }) => {
      if (options) {
        onOpenChange({
          id,
          value
        });
      }
      if (value && onClick) {
        onClick();
      }
    },
    buttonClassName: 'editor-menu-bar-button',
    buttonText: title,
    options: dropdownOptions,
    'data-test-id': `menu-bar-${id}`,
    disabled
  }, dropdownProps);
  let dropdown;
  if (id === HELP_OPTION_ID) {
    dropdown = /*#__PURE__*/_jsx(EditorHelpMenuDropdown, Object.assign({
      topLevelMenuOptions: topLevelMenuOptions,
      objectTypeId: objectTypeId,
      flow: flow,
      onOptionClick: onOptionClick
    }, defaultDropdownProps));
  } else {
    dropdown = /*#__PURE__*/_jsx(UIMultiLevelDropdown, Object.assign({}, defaultDropdownProps));
  }
  const DropdownContainer = Container || StyledDropdownContainer;
  return /*#__PURE__*/_jsx(DropdownContainer, {
    ref: rootRef,
    open: open,
    disabled: disabled,
    onMouseEnter: () => {
      if (onMouseEnter) {
        onMouseEnter({
          id
        });
      }
    },
    onMouseLeave: () => {
      if (onMouseLeave) {
        onMouseLeave({
          id
        });
      }
    },
    children: dropdown
  });
};
export default EditorMenuBarButton;